'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface TooltipProps {
  title: string;
  definition?: string;
  assessmentCriteria?: string[];
  scoringGuidelines?: { range: string; multiplier?: string; level?: string; description: string }[];
  impact?: string;
  wide?: boolean; // Deprecated - tooltips now auto-size to content
}

export default function Tooltip({
  title,
  definition,
  assessmentCriteria,
  scoringGuidelines,
  impact,
  wide,
}: TooltipProps) {
  const { darkMode } = useDarkMode();

  // Special tooltips get extra width
  const isIndustrySector = title === 'Industry Sector Typical Ranges';
  const isQuantitativeBenchmarks = title === 'Quantitative Data Benchmarks';
  const isQualitativeMultiplier = title === 'Qualitative Multiplier Reference';
  const tooltipWidth = isQualitativeMultiplier
    ? '640px'
    : isIndustrySector || isQuantitativeBenchmarks
      ? '560px'
      : wide
        ? '480px'
        : '380px';

  const tooltipContent = (
    <div
      style={{
        width: tooltipWidth,
        maxWidth: tooltipWidth,
        boxSizing: 'border-box',
      }}
      className={`rounded-xl p-3 text-xs leading-snug backdrop-blur-sm sm:p-4 sm:text-sm ${
        darkMode
          ? 'bg-gray-900/95 shadow-2xl ring-1 ring-white/10'
          : 'bg-white/95 shadow-2xl ring-1 ring-black/10'
      } `}
    >
      {/* Title */}
      <div
        className={`mb-3 pb-2 text-[15px] font-semibold ${
          darkMode
            ? 'border-b border-white/10 text-gray-100'
            : 'border-b border-black/10 text-gray-900'
        } `}
      >
        {title}
      </div>

      {/* Definition */}
      {definition && (
        <div className="mb-3">
          <div
            className={`mb-1 text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            Definition:
          </div>
          <div className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{definition}</div>
        </div>
      )}

      {/* Assessment Criteria */}
      {assessmentCriteria && assessmentCriteria.length > 0 && (
        <div className="mb-3">
          <div
            className={`mb-1 text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            Assessment Criteria:
          </div>
          <ul className={`m-0 pl-5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {assessmentCriteria.map((criteria, index) => (
              <li key={index} className="mb-0.5">
                {criteria}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Scoring Guidelines */}
      {scoringGuidelines && scoringGuidelines.length > 0 && (
        <div className={impact ? 'mb-3' : ''}>
          <div
            className={`mb-2 text-xs font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
          >
            Scoring Guidelines:
          </div>
          {/* Check if this is a simple pricing guideline (Low/Medium/High/Premium) */}
          {scoringGuidelines.every((g) =>
            ['Low', 'Medium', 'High', 'Premium'].includes(g.description)
          ) ? (
            <div className={`text-[11px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {scoringGuidelines.map((guideline, index) => (
                <div
                  key={index}
                  className={`py-1 ${
                    index < scoringGuidelines.length - 1
                      ? darkMode
                        ? 'border-b border-white/5'
                        : 'border-b border-black/5'
                      : ''
                  } `}
                >
                  <span className="font-semibold">{guideline.range}</span>
                  <span className={`ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    ({guideline.description})
                  </span>
                </div>
              ))}
            </div>
          ) : (
            (() => {
              // Check if we need to split into two tables (items with level vs without)
              const itemsWithLevel = scoringGuidelines.filter(
                (g) => g.level && g.level.trim() !== ''
              );
              const itemsWithoutLevel = scoringGuidelines.filter(
                (g) => !g.level || g.level.trim() === ''
              );
              const shouldSplit = itemsWithLevel.length > 0 && itemsWithoutLevel.length > 0;

              if (shouldSplit) {
                // Two-table layout
                const isQuantitativeBenchmarks = title === 'Quantitative Data Benchmarks';
                return (
                  <div className="w-full space-y-3">
                    {/* First table: Items with level (two columns) */}
                    <table
                      className={`w-full border-collapse text-[11px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      <tbody>
                        {itemsWithLevel.map((guideline, index) => (
                          <tr
                            key={index}
                            className={` ${
                              index < itemsWithLevel.length - 1
                                ? darkMode
                                  ? 'border-b border-white/5'
                                  : 'border-b border-black/5'
                                : ''
                            } `}
                          >
                            <td className="px-2 py-1.5 align-top" style={{ width: '25%' }}>
                              <div
                                className="font-semibold"
                                style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                              >
                                {guideline.range}
                                {guideline.multiplier && (
                                  <span
                                    className={`ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                                  >
                                    ({guideline.multiplier})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 align-top" style={{ width: '75%' }}>
                              {isQuantitativeBenchmarks ? (
                                // For Quantitative Data Benchmarks: show level and description on same line
                                <div
                                  className="text-[10px]"
                                  style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                                >
                                  {guideline.level && (
                                    <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                                      {guideline.level}
                                    </span>
                                  )}
                                  {guideline.level && guideline.description && <span> - </span>}
                                  {guideline.description && <span>{guideline.description}</span>}
                                </div>
                              ) : (
                                // For other tooltips: show level and description on separate lines
                                <>
                                  {guideline.level && (
                                    <div
                                      className={`mb-0.5 text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                                      style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                                    >
                                      {guideline.level}
                                    </div>
                                  )}
                                  {guideline.description && (
                                    <div
                                      className="text-[10px]"
                                      style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                                    >
                                      {guideline.description}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Second table: Items without level (single column) */}
                    <table
                      className={`w-full border-collapse text-[11px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      <tbody>
                        {itemsWithoutLevel.map((guideline, index) => (
                          <tr
                            key={index}
                            className={` ${
                              index < itemsWithoutLevel.length - 1
                                ? darkMode
                                  ? 'border-b border-white/5'
                                  : 'border-b border-black/5'
                                : ''
                            } `}
                          >
                            <td className="px-2 py-1.5">
                              <div
                                className="font-semibold"
                                style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                              >
                                {guideline.range}
                                {guideline.multiplier && (
                                  <span
                                    className={`ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                                  >
                                    {guideline.multiplier}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              } else {
                // Single table layout (existing logic)
                const isIndustrySector = scoringGuidelines.some((g) =>
                  g.description?.includes(':')
                );
                const isTvSize = title === 'TV Size Multiplier';
                const isQualitativeMultiplier = title === 'Qualitative Multiplier Reference';
                const firstColWidth = isIndustrySector
                  ? '35%'
                  : isTvSize
                    ? '30%'
                    : isQualitativeMultiplier
                      ? '25%'
                      : '10%';
                const secondColWidth = isIndustrySector
                  ? '65%'
                  : isTvSize
                    ? '70%'
                    : isQualitativeMultiplier
                      ? '75%'
                      : '90%';

                return (
                  <div className="w-full">
                    <table
                      className={`w-full border-collapse text-[11px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      <tbody>
                        {scoringGuidelines.map((guideline, index) => (
                          <tr
                            key={index}
                            className={` ${
                              index < scoringGuidelines.length - 1
                                ? darkMode
                                  ? 'border-b border-white/5'
                                  : 'border-b border-black/5'
                                : ''
                            } `}
                          >
                            <td className="px-2 py-1.5 align-top" style={{ width: firstColWidth }}>
                              <div
                                className="font-semibold"
                                style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                              >
                                {guideline.range}
                                {guideline.multiplier && (
                                  <span
                                    className={`ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                                  >
                                    ({guideline.multiplier})
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-1.5 align-top" style={{ width: secondColWidth }}>
                              {isQualitativeMultiplier ? (
                                // For Qualitative Multiplier: show level and description on same line
                                <div
                                  className="text-[10px]"
                                  style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                                >
                                  {guideline.level && (
                                    <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                                      {guideline.level}
                                    </span>
                                  )}
                                  {guideline.level && guideline.description && <span> - </span>}
                                  {guideline.description && <span>{guideline.description}</span>}
                                </div>
                              ) : (
                                // For other tooltips: show level and description on separate lines
                                <>
                                  {guideline.level && (
                                    <div
                                      className={`mb-0.5 text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                                      style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                                    >
                                      {guideline.level}
                                    </div>
                                  )}
                                  {guideline.description && (
                                    <div
                                      className="text-[10px]"
                                      style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}
                                    >
                                      {guideline.description}
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }
            })()
          )}
        </div>
      )}

      {/* Impact */}
      {impact && (
        <div
          className={`rounded-md p-2 text-xs italic ${
            darkMode ? 'bg-green-500/10 text-green-400' : 'bg-green-500/10 text-green-800'
          } `}
        >
          {impact}
        </div>
      )}

      {/* Arrow */}
      <TooltipPrimitive.Arrow
        className={`${darkMode ? 'fill-gray-900/95' : 'fill-white/95'}`}
        width={16}
        height={8}
      />
    </div>
  );

  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <button
            type="button"
            className={`ml-1.5 inline-flex h-4.5 w-4.5 cursor-pointer items-center justify-center rounded-full border-0 p-0 text-[11px] font-semibold transition-all duration-200 ${
              darkMode
                ? 'bg-white/10 text-gray-400 hover:bg-white/15'
                : 'bg-black/5 text-gray-600 hover:bg-black/10'
            } `}
          >
            ?
          </button>
        </TooltipPrimitive.Trigger>

        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            className="z-[9999]"
            style={{
              width: tooltipWidth,
              maxWidth: tooltipWidth,
            }}
            sideOffset={8}
            side="top"
            align="center"
            collisionPadding={10}
          >
            {tooltipContent}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
